/**
 * Utility functions for tag filtering in the analysis page
 */

/**
 * Extract tags from event comment
 * Tags are stored in the format [tag1, tag2, tag3] at the beginning of the comment
 * 
 * @param {string} comment - The event comment
 * @returns {string[]} - Array of extracted tags
 */
export const extractTagsFromComment = (comment) => {
  if (!comment) return [];
  
  // Match content inside square brackets at the beginning of the comment
  const tagMatch = comment.match(/^\[(.*?)\]/);
  if (!tagMatch || !tagMatch[1]) return [];
  
  // Split by comma and trim each tag
  return tagMatch[1].split(',').map(tag => tag.trim());
};

/**
 * Get all unique tags from events
 * 
 * @param {Array} events - Array of event objects
 * @returns {string[]} - Array of unique tags
 */
export const getAllUniqueTags = (events) => {
  if (!events || !events.length) return [];
  
  // Extract tags from all events
  const allTags = events.flatMap(event => 
    extractTagsFromComment(event.comment)
  );
  
  // Return unique tags
  return [...new Set(allTags)];
};

/**
 * Get tags relevant to a specific event type
 * 
 * @param {Array} events - Array of event objects
 * @param {string} eventType - The event type to filter by
 * @returns {string[]} - Array of unique tags for the specified event type
 */
export const getTagsForEventType = (events, eventType) => {
  if (!events || !events.length || !eventType) return [];
  
  // Filter events by event type
  const filteredEvents = events.filter(event => event.eventType === eventType);
  
  // Extract tags from filtered events
  return getAllUniqueTags(filteredEvents);
};

/**
 * Check if an event has any of the specified tags
 * 
 * @param {Object} event - The event object
 * @param {string[]} selectedTags - Array of selected tag strings
 * @returns {boolean} - True if the event has any of the selected tags
 */
export const eventHasAnyTag = (event, selectedTags) => {
  if (!selectedTags || !selectedTags.length) return true;
  if (!event.comment) return false;
  
  const eventTags = extractTagsFromComment(event.comment);
  return selectedTags.some(tag => eventTags.includes(tag));
};